/**
 * Connectors Page – external accounts the team connects, one platform at a time
 */
import type { ComponentType, KeyboardEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslations } from "@/hooks/use-translations";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { MetaIcon, TikTokIcon } from "@/components/icons/customIcons";
import { MetaConnector } from "@/components/connectors/meta-connector";

interface Platform {
	id: string;
	labelKey: string;
	icon: ComponentType<{ size?: number; className?: string }>;
	// Platforms without a panel are listed but show "coming soon".
	panel?: ComponentType;
}

const PLATFORMS: Platform[] = [
	{ id: "meta", labelKey: "metaAds", icon: MetaIcon, panel: MetaConnector },
	{ id: "tiktok", labelKey: "tiktokAds", icon: TikTokIcon },
];

const tabId = (id: string) => `connector-tab-${id}`;
const panelId = (id: string) => `connector-panel-${id}`;

export function ConnectorsPage() {
	const t = useTranslations("connectors");
	const tCommon = useTranslations("common");
	// The selected platform lives in the URL so a link can open it directly.
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedIndex = Math.max(
		0,
		PLATFORMS.findIndex((p) => p.id === searchParams.get("platform")),
	);
	const selected = PLATFORMS[selectedIndex];
	const Panel = selected.panel;

	const select = (id: string) =>
		setSearchParams({ platform: id }, { replace: true });

	// Arrow keys move between tabs, as in any tab list.
	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
		if (!step) return;
		e.preventDefault();
		const next = PLATFORMS[(selectedIndex + step + PLATFORMS.length) % PLATFORMS.length];
		select(next.id);
		document.getElementById(tabId(next.id))?.focus();
	};

	return (
		<div className="min-h-screen p-6 bg-bg-canvas">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<h1 className="text-2xl font-semibold text-text-primary">{t("title")}</h1>
					<p className="text-text-tertiary">{t("description")}</p>
				</div>

				<div
					role="tablist"
					aria-label={t("platforms")}
					onKeyDown={handleKeyDown}
					className="mb-4 flex gap-1 overflow-x-auto rounded-xl border border-border-subtle bg-bg-surface p-1 scrollbar-thin"
				>
					{PLATFORMS.map((platform) => {
						const isSelected = platform.id === selected.id;
						const Icon = platform.icon;
						return (
							<button
								key={platform.id}
								type="button"
								role="tab"
								id={tabId(platform.id)}
								aria-selected={isSelected}
								aria-controls={panelId(platform.id)}
								tabIndex={isSelected ? 0 : -1}
								onClick={() => select(platform.id)}
								className={cn(
									// Equal share of the bar for every platform; never narrower than its label.
									"group flex min-w-max flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2",
									// Same highlight as the active sidebar item (GUIDE rule 6).
									isSelected
										? "bg-nav-active-bg font-semibold text-text-primary"
										: "font-medium text-text-secondary hover:bg-bg-canvas hover:text-text-primary",
								)}
							>
								<Icon
									size={18}
									className={
										isSelected
											? "text-text-primary"
											: "text-text-secondary group-hover:text-text-primary"
									}
								/>
								{t(platform.labelKey)}
							</button>
						);
					})}
				</div>

				<div
					role="tabpanel"
					id={panelId(selected.id)}
					aria-labelledby={tabId(selected.id)}
				>
					{Panel ? (
						<Panel />
					) : (
						<Card>
							<div className="h-64 flex items-center justify-center text-text-tertiary">
								{tCommon("comingSoon")}
							</div>
						</Card>
					)}
				</div>
			</div>
		</div>
	);
}
